import { updateProductVariantPrice } from '@/api/sale-api';
import { toast } from '@/hooks/use-toast';
import { AdjustInStockDialog } from '@/pages/stock/components/AdjustInStockDialog';
import { AdjustOutStockDialog } from '@/pages/stock/components/AdjustOutStockDialog';
import { ProductVariant } from '@/types/product-type';
import { PinnedVariantWithStock } from '@/types/sale-type';
import { useCallback, useState } from 'react';
import { useLocation } from 'wouter';
import { OrderInlineAlert } from '../OrderInlineAlert';
import { PriceEditDialog } from '../price-edit-dialog';
import { PinnedOrderTable } from './pinned-order-table';
import { PinnedVariantHeader } from './pinned-variant-header';
import { useDropSlotViewport } from './use-drop-slot-viewport';
import { useVariantAssignDrop } from './use-variant-assign-drop';
import { useVariantOrders } from './use-variant-orders';
import { useVariantSlotMetrics } from './use-variant-slot-metrics';

export type VariantQuantityRowsProps = {
  variant: PinnedVariantWithStock;
  orderDate?: string;
  onAssignToSlot?: (args: {
    payload: Record<string, unknown>;
    variant: PinnedVariantWithStock;
  }) => Promise<boolean>;
  onRefresh?: (variantId: string) => void;
};

export function VariantQuantityRows({
  variant,
  onAssignToSlot,
  orderDate,
  onRefresh,
}: VariantQuantityRowsProps) {
  const [, setLocation] = useLocation();
  const [adjustInOpen, setAdjustInOpen] = useState(false);
  const [adjustOutOpen, setAdjustOutOpen] = useState(false);
  const [currentProductVariant, setCurrentProductVariant] = useState<ProductVariant | null>(null);

  const {
    remain,
    setRemain,
    orderForVariants,
    setOrderForVariants,
    lastOrderEvent,
    newOrderCount,
    isCreatingOrderRef,
    refreshOrdersAndRemain,
  } = useVariantOrders({
    variantId: variant.id,
    initialRemain: variant.remain,
    orderDate,
  });

  const { effectiveRemain, overflow, rows, orderRowsHeight, nextDropSlotNo } =
    useVariantSlotMetrics(remain, variant.remain, orderForVariants.length);

  const assignDrop = useVariantAssignDrop({
    variant,
    orderCount: orderForVariants.length,
    orderRowsHeight,
    nextDropSlotNo,
    isCreatingOrderRef,
    setOrderForVariants,
    setRemain,
    onAssignToSlot,
    onRefreshAfterAssign: refreshOrdersAndRemain,
  });

  const { virtualRange, handleScroll } = useDropSlotViewport({
    scrollContainerRef: assignDrop.scrollContainerRef,
    rows,
    orderRowsHeight,
    variantId: variant.id,
    visibleCount: rows.length,
  });

  const handleSavePrice = useCallback(
    async (newPrice: number) => {
      if (!currentProductVariant) return;

      const resp = await updateProductVariantPrice(currentProductVariant.id, newPrice);
      if (resp.success) {
        setCurrentProductVariant(null);
        toast({ title: 'Success', description: 'Product price updated successfully' });
        onRefresh?.(currentProductVariant.id);
      } else {
        toast({
          title: 'Error',
          description: resp.message || 'Product price updated failed',
          variant: 'destructive',
        });
      }
    },
    [currentProductVariant, onRefresh],
  );

  const headerImageUrl = variant.variant.imageUrl || variant.product.imageUrl;
  const stock = variant.variant.stock;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden p-2 sm:p-3">
      {currentProductVariant && (
        <PriceEditDialog
          productVariant={currentProductVariant}
          onSave={handleSavePrice}
          key={currentProductVariant.id}
          trigger={currentProductVariant.productName}
          open
          onOpenChange={(open) => {
            if (!open) setCurrentProductVariant(null);
          }}
        />
      )}

      {stock && (
        <AdjustInStockDialog
          isOpen={adjustInOpen}
          onClose={() => setAdjustInOpen(false)}
          onSuccess={() => onRefresh?.(variant.id)}
          stock={stock}
          sku={variant.variant.sku}
        />
      )}

      {stock && (
        <AdjustOutStockDialog
          isOpen={adjustOutOpen}
          onClose={() => setAdjustOutOpen(false)}
          onSuccess={() => onRefresh?.(variant.id)}
          stock={stock}
          sku={variant.variant.sku}
        />
      )}

      <PinnedVariantHeader
        name={variant.variant.name}
        sku={variant.variant.sku}
        imageUrl={headerImageUrl}
        price={variant.variant.price || 0}
        remain={effectiveRemain}
        isCreatingOrder={assignDrop.isCreatingOrder}
        onEditPrice={() => setCurrentProductVariant(variant.variant)}
        onAdjustIn={() => setAdjustInOpen(true)}
        onAdjustOut={() => setAdjustOutOpen(true)}
      />

      <OrderInlineAlert event={lastOrderEvent} count={newOrderCount} />

      <PinnedOrderTable
        scrollContainerRef={assignDrop.mergeScrollRef}
        isAssignDragActive={assignDrop.isAssignDragActive}
        isDragOverTable={assignDrop.isDragOverTable}
        isCreatingOrder={assignDrop.isCreatingOrder}
        orderForVariants={orderForVariants}
        virtualRange={virtualRange}
        targetDropSlotNo={assignDrop.targetDropSlotNo}
        dragPreviewName={assignDrop.dragPreviewName}
        selectedAssignActive={assignDrop.selectedAssignActive}
        nextDropSlotNo={nextDropSlotNo}
        overflow={overflow}
        variantId={variant.id}
        variantName={variant.variant.name}
        onScroll={handleScroll}
        onTapAssign={assignDrop.handleTapAssign}
        onOrderClick={(row) => setLocation(`/orders/${row.id}`)}
        onOrderRefresh={() => void refreshOrdersAndRemain()}
      />
    </div>
  );
}
